import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Print,
  Refresh,
} from '@mui/icons-material';
import { taxBooksService } from '../../../../services/api';

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const DTEAnulacionWizard = () => {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    branchId: '',
  });
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadBranches = async () => {
    try {
      const branchRows = await taxBooksService.getCatalog('branches');
      setBranches(branchRows || []);
    } catch {
      setBranches([]);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await taxBooksService.getPreview('books/annulments/preview', filters);
      setRows(response.rows || []);
      setSummary(response.summary || {});
    } catch (err) {
      console.error('Error loading annulments:', err);
      setError(err?.response?.data?.error || err.message || 'No se pudieron cargar las anulaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    const selectedBranch = branches.find((branch) => String(branch.id) === String(filters.branchId));
    const branchLabel = selectedBranch
      ? `${selectedBranch.branch_name} (${selectedBranch.codigo_establecimiento})`
      : 'Todas';
    const monthLabel = String(filters.month).padStart(2, '0');
    const yearLabel = filters.year;
    const printedAt = new Date().toLocaleString('es-SV');

    const tableRows = rows.length > 0
      ? rows
          .map(
            (row) => `
              <tr>
                <td>${escapeHtml(formatCell(row.fecha_emision))}</td>
                <td>${escapeHtml(formatCell(row.codigo_generacion))}</td>
                <td>${escapeHtml(formatCell(row.numero_control))}</td>
                <td>${escapeHtml(formatCell(row.tipo_documento))}</td>
                <td>${escapeHtml(formatCell(row.tipo_detalle_documento))}</td>
                <td>${escapeHtml(formatCell(row.motivo_anulacion))}</td>
              </tr>
            `
          )
          .join('')
      : `
          <tr>
            <td colspan="6" style="text-align:center;">No se encontraron documentos anulados para el período seleccionado.</td>
          </tr>
        `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) return;

    printWindow.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <title>Consulta de Anulaciones</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              color: #111827;
              margin: 24px;
            }
            h1 {
              font-size: 22px;
              margin: 0 0 8px;
            }
            p {
              margin: 0 0 4px;
            }
            .meta {
              margin: 16px 0;
              display: grid;
              grid-template-columns: repeat(2, minmax(220px, 1fr));
              gap: 8px 16px;
            }
            .summary {
              margin: 16px 0;
              padding: 12px;
              border: 1px solid #d1d5db;
              width: fit-content;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 16px;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 8px;
              font-size: 12px;
              vertical-align: top;
              text-align: left;
            }
            th {
              background: #f3f4f6;
            }
            .mono {
              font-family: "Courier New", monospace;
            }
            @media print {
              body {
                margin: 12mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>Consulta de Anulaciones</h1>
          <p>Reporte de documentos anulados por período.</p>
          <div class="meta">
            <p><strong>Período:</strong> ${escapeHtml(`${monthLabel}/${yearLabel}`)}</p>
            <p><strong>Sucursal:</strong> ${escapeHtml(branchLabel)}</p>
            <p><strong>Impreso el:</strong> ${escapeHtml(printedAt)}</p>
          </div>
          <div class="summary">
            <strong>Documentos anulados:</strong> ${escapeHtml(summary.count || 0)}
          </div>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Código de Generación</th>
                <th>Número de Control</th>
                <th>Tipo DTE</th>
                <th>Tipo Detalle</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            Consulta de Anulaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vista de documentos anulados por período. Esta pantalla es solo de consulta.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<Print />} onClick={handlePrint} disabled={loading}>
            Imprimir
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={filters.month}
                onChange={(event) => setFilters((prev) => ({ ...prev, month: event.target.value }))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index + 1} value={String(index + 1)}>
                    {String(index + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Año"
                value={filters.year}
                onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filters.branchId}
                onChange={(event) => setFilters((prev) => ({ ...prev, branchId: event.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadData} sx={{ height: '100%' }}>
                Consultar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Documentos anulados
              </Typography>
              <Typography variant="h5">
                {summary.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="info">
              No se encontraron documentos anulados para el período seleccionado.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Código de Generación</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Número de Control</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo DTE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo Detalle</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.codigo_generacion || row.numero_control || index}-${index}`}>
                      <TableCell>{formatCell(row.fecha_emision)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{formatCell(row.codigo_generacion)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{formatCell(row.numero_control)}</TableCell>
                      <TableCell>{formatCell(row.tipo_documento)}</TableCell>
                      <TableCell>{formatCell(row.tipo_detalle_documento)}</TableCell>
                      <TableCell>{formatCell(row.motivo_anulacion)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DTEAnulacionWizard;
